import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ActionDropdown } from '@/components';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  dateCls,
  LoadError,
  MappedBadge,
  nameCls,
  numCls,
  PageHeader,
} from '../../components/ui';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { STOCK_OPNAME_FILTERS } from '../../filterFields';
import { fmtDateTime } from '../../format';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { WAREHOUSE_PATHS } from '../../paths';
import {
  OPNAME_STATUS,
  OPNAME_TAB_LABEL,
  OPNAME_TAB_STATUSES,
  OPNAME_TABS,
  OPNAME_TYPE_META,
  type OpnameTab,
} from '../../status';
import type { StockOpnameStatusSummary, StockOpnameSummary } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { fmtGap, gapCls } from './opnameDraft';

/** Rows behind one tab: its status (or both rejected ones) from the summary. */
const tabCount = (
  summary: StockOpnameStatusSummary,
  tab: OpnameTab
): number => {
  const statuses = OPNAME_TAB_STATUSES[tab];
  return statuses
    ? statuses.reduce((t, s) => t + (summary.counts[s] ?? 0), 0)
    : summary.total;
};

const StockOpnameListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  const q = useListQuery<OpnameTab>(OPNAME_TABS, 'ALL', STOCK_OPNAME_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;
  const search = q.search.committed || null;
  const common = { hubID, ...filterApi, search };
  const statuses = OPNAME_TAB_STATUSES[q.tab];

  const list = useLoad(
    (signal) =>
      warehouseApi.listStockOpnames(
        {
          ...common,
          status: statuses ? statuses.join(',') : null,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, filterKey, search, q.tab, q.page, q.limit, refreshToken],
    ready
  );
  // Tab counts: the list's filters (minus the tab) — never disagree with it.
  const summary = useLoad(
    (signal) => warehouseApi.getStockOpnameSummary(common, { signal }),
    [hubID, filterKey, search, refreshToken],
    ready
  );

  const tabs = OPNAME_TABS.map((k) => ({
    key: k,
    label: OPNAME_TAB_LABEL[k],
    count: summary.data ? tabCount(summary.data, k) : null,
  }));

  const columns = useMemo<TableDataColumn<StockOpnameSummary>[]>(
    () => [
      {
        key: 'code',
        title: 'Kode',
        render: (_v, o) => <span className={codeCls}>{o.code}</span>,
      },
      {
        key: 'client',
        title: 'Client',
        render: (_v, o) => (
          <span className={nameCls}>{o.client?.name ?? `#${o.clientID}`}</span>
        ),
      },
      {
        key: 'type',
        title: 'Tipe',
        render: (_v, o) => <MappedBadge map={OPNAME_TYPE_META} value={o.type} />,
      },
      {
        key: 'lines',
        title: 'Baris · selisih',
        align: 'right',
        render: (_v, o) => (
          <span className={numCls}>
            {o.gapLineCount} / {o.lineCount}
          </span>
        ),
      },
      {
        key: 'totalGap',
        title: 'Total gap',
        align: 'right',
        render: (_v, o) => (
          <span
            className={`font-mono text-[12px] font-semibold tabular-nums ${gapCls(o.totalGap)}`}
          >
            {fmtGap(o.totalGap)}
          </span>
        ),
      },
      {
        key: 'createdBy',
        title: 'Pemohon',
        render: (_v, o) => <span className={nameCls}>{o.createdBy}</span>,
      },
      {
        key: 'createdAt',
        title: 'Tanggal',
        render: (_v, o) => (
          <span className={dateCls}>{fmtDateTime(o.createdAt)}</span>
        ),
      },
      {
        key: 'status',
        title: 'Status',
        render: (_v, o) => <MappedBadge map={OPNAME_STATUS} value={o.status} />,
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, o) => (
          <ActionDropdown
            actions={[
              {
                key: 'view',
                label: 'Lihat detail',
                icon: 'ri-eye-line',
                onClick: () => navigate(`${WAREHOUSE_PATHS.stockOpname}/${o.id}`),
              },
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Warehouse management"
        title="Stock opname"
        actions={
          <HeaderActions>
            <Button
              data-testid="opname-create"
              onClick={() => navigate(`${WAREHOUSE_PATHS.stockOpname}/new`)}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Buat stock opname
            </Button>
          </HeaderActions>
        }
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Cari kode, storage, SKU…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter stock opname"
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading || !ready}
            data={list.data?.data ?? []}
            columns={columns}
            onRowClick={(o) => navigate(`${WAREHOUSE_PATHS.stockOpname}/${o.id}`)}
            emptyState={<EmptyTable title="Belum ada stock opname" />}
          />
        )}
      </ListShell>
    </div>
  );
};

export default StockOpnameListPage;
